import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryLPC } from 'app/lib/query';
import { http } from '../lib/axios';
import { interestKeys } from './keys';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { Interest, CreateInterestDTO, CreateManyInterestsDTO, UpdateInterestDTO } from 'app/types/interest';
import { isLoggedInAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';

export function useAllInterests(opts: FunctionalUseQueryOptions<Interest[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  return useQueryLPC<Interest[]>({
    queryKey: interestKeys.lists(),
    queryFn: () => http.get<Interest[]>('/interests').then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });
}

export function useOneInterest(id: string, opts: FunctionalUseQueryOptions<Interest> = {}) {
  return useQueryLPC<Interest>({
    queryKey: interestKeys.detail(id),
    queryFn: () => http.get<Interest>(`/interests/${id}`).then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && !!id,
  });
}

export function useCreateOneInterest(opts: UseMutationOptions<Interest, Error, CreateInterestDTO> = {}) {
  const client = useQueryClient();
  return useMutation<Interest, Error, CreateInterestDTO>({
    mutationFn: (payload) => http.post<Interest>('/interests', payload).then((res) => res.data),
    ...opts,
    onSuccess: async (data, variables, context) => {
      client.setQueryData<Interest[] | undefined>(interestKeys.lists(), (previous) => {
        const next =
          previous?.filter(
            (interest) =>
              !(interest.id === data.id || (interest.tagId === data.tagId && interest.action === data.action)),
          ) ?? [];
        return [...next, data];
      });
      // Mark stale so it refreshes on next focus/mount, but don't trigger an
      // immediate refetch — the cache is already up to date via setQueryData,
      // and refetching here would flicker any screen currently rendering the list.
      await client.invalidateQueries({ queryKey: interestKeys.lists(), refetchType: 'none' });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useCreateManyInterests(opts: UseMutationOptions<Interest[], Error, CreateManyInterestsDTO> = {}) {
  const client = useQueryClient();
  return useMutation<Interest[], Error, CreateManyInterestsDTO>({
    mutationFn: (payload) => http.post<Interest[]>('/interests/many', payload).then((res) => res.data),
    ...opts,
    onSuccess: async (data, variables, context) => {
      await client.invalidateQueries({ queryKey: interestKeys.lists() });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateOneInterest(
  opts: UseMutationOptions<Interest, Error, { id: string } & UpdateInterestDTO> = {},
) {
  const client = useQueryClient();
  return useMutation<Interest, Error, { id: string } & UpdateInterestDTO>({
    mutationFn: ({ id, ...payload }) => http.patch<Interest>(`/interests/${id}`, payload).then((res) => res.data),
    ...opts,
    onSuccess: async (data, variables, context) => {
      client.setQueryData(interestKeys.detail(variables.id), data);
      await client.invalidateQueries({ queryKey: interestKeys.lists() });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useDeleteOneInterest(opts: UseMutationOptions<undefined, Error, string> = {}) {
  const client = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (id) => http.delete(`/interests/${id}`).then(() => undefined),
    ...opts,
    onSuccess: async (data, id, context) => {
      client.setQueryData<Interest[] | undefined>(interestKeys.lists(), (previous) => {
        return previous?.filter((interest) => interest.id !== id) ?? previous;
      });
      client.removeQueries({ queryKey: interestKeys.detail(id) });
      await client.invalidateQueries({ queryKey: interestKeys.lists(), refetchType: 'none' });
      return opts.onSuccess?.(data, id, context);
    },
  });
}
